import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import type { Workstream } from '../types/workstream';
import type { GoogleUser } from '../types/auth';
import type { AssigneeFilter, TaskFilterState } from '../types/taskFilter';
import WorkstreamCard from '../components/WorkstreamCard';
import WorkstreamFilterPanel from '../components/WorkstreamFilterPanel';

const buttonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '8px 16px',
  backgroundColor: '#1a1a1a',
  color: '#fff',
  border: 'none',
  borderRadius: '20px',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
};

// --- localStorage helpers ---
function loadBool(key: string, fallback: boolean): boolean {
  try { const v = localStorage.getItem(key); return v === null ? fallback : v === 'true'; } catch { return fallback; }
}
function saveBool(key: string, v: boolean) {
  try { localStorage.setItem(key, String(v)); } catch { /* ignore */ }
}
function loadStringArray(key: string): string[] | null {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : null; } catch { return null; }
}
function saveStringArray(key: string, v: string[]) {
  try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* ignore */ }
}

type PageState = 'loading' | 'no-workstreams-yet' | 'no-workstreams-selected' | 'workstreams-view';

export default function WorkstreamsPage() {
  const navigate = useNavigate();
  const [pageState, setPageState] = useState<PageState>('loading');
  // allWorkstreams includes archived; activeWorkstreams excludes them
  const [allWorkstreams, setAllWorkstreams] = useState<Workstream[]>([]);
  const [activeWorkstreams, setActiveWorkstreams] = useState<Workstream[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);
  const generationRef = useRef(0);

  // Filter state — persisted in localStorage
  const [showAll, setShowAllState] = useState(() => loadBool('workstreams.showAll', true));
  const [showArchived, setShowArchivedState] = useState(() => loadBool('workstreams.showArchived', false));
  const [selectedWithArchived, setSelectedWithArchivedState] = useState<string[] | null>(() => loadStringArray('workstreams.selectedWithArchived'));
  const [selectedWithoutArchived, setSelectedWithoutArchivedState] = useState<string[] | null>(() => loadStringArray('workstreams.selectedWithoutArchived'));

  const setShowAll = useCallback((v: boolean) => { setShowAllState(v); saveBool('workstreams.showAll', v); }, []);
  const setShowArchived = useCallback((v: boolean) => { setShowArchivedState(v); saveBool('workstreams.showArchived', v); }, []);
  const setSelectedWithArchived = useCallback((v: string[]) => { setSelectedWithArchivedState(v); saveStringArray('workstreams.selectedWithArchived', v); }, []);
  const setSelectedWithoutArchived = useCallback((v: string[]) => { setSelectedWithoutArchivedState(v); saveStringArray('workstreams.selectedWithoutArchived', v); }, []);

  // Task filter state
  const [taskSearch, setTaskSearch] = useState('');
  const [taskAssignees, setTaskAssignees] = useState<Set<AssigneeFilter>>(new Set());
  const [showArchivedTasks, setShowArchivedTasks] = useState(false);

  const handleToggleTaskAssignee = useCallback((a: AssigneeFilter) => {
    setTaskAssignees((prev) => {
      const next = new Set(prev);
      if (next.has(a)) next.delete(a); else next.add(a);
      return next;
    });
  }, []);

  const taskFilter = useMemo<TaskFilterState>(() => ({
    search: taskSearch,
    assignees: taskAssignees,
    showArchivedTasks,
  }), [taskSearch, taskAssignees, showArchivedTasks]);

  // User info for assignee filter
  const user = useMemo<GoogleUser | null>(() => {
    try { const s = Cookies.get('User'); return s ? JSON.parse(s) : null; } catch { return null; }
  }, []);
  const userInitials = useMemo(() => {
    if (!user) return '?';
    const f = (user.given_name ?? '')[0] ?? '';
    const l = (user.family_name ?? '')[0] ?? '';
    return (f + l).toUpperCase() || user.name?.[0]?.toUpperCase() || '?';
  }, [user]);
  const userTenantID = Cookies.get('TenantID') ?? '';

  const loadWorkstreams = useCallback(async () => {
    generationRef.current++;
    const gen = generationRef.current;
    try {
      // Fetch active workstreams first — this is the critical request.
      const resp = await fetch('/api/workstreams');
      if (gen !== generationRef.current) return;
      if (!resp.ok) {
        setError((await resp.text()) || 'Failed to load workstreams');
        return;
      }
      const active: Workstream[] = (await resp.json()) ?? [];
      if (gen !== generationRef.current) return;

      setActiveWorkstreams(active);
      // Render immediately with active data — don't wait for the
      // archived fetch. Use active list as the initial allWorkstreams
      // so the page is usable right away.
      setAllWorkstreams(active);
      setPageState(active.length === 0 ? 'no-workstreams-yet' : 'workstreams-view');

      // Fire the archived-list fetch in the background. When it
      // completes, update allWorkstreams so "Show Archived" and the
      // "no workstreams yet" vs "no workstreams selected" distinction
      // become accurate. A failure is silently ignored.
      fetch('/api/workstreams?includeDeleted=true')
        .then(async (permResp) => {
          if (gen !== generationRef.current || !permResp.ok) return;
          const all: Workstream[] = (await permResp.json()) ?? [];
          if (gen !== generationRef.current) return;
          setAllWorkstreams(all);
          if (active.length === 0 && all.length > 0) {
            // Archived workstreams exist — use workstreams-view so
            // the filter/effectivePageState logic can decide whether
            // to show cards (showArchived=true) or the "no workstreams
            // selected" empty state.
            setPageState('workstreams-view');
          }
        })
        .catch(() => { /* auxiliary — ignore */ });
    } catch (err: unknown) {
      if (gen === generationRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to load workstreams');
      }
    }
  }, []);

  useEffect(() => { loadWorkstreams(); }, [loadWorkstreams]);

  // The "candidate" list for the current showArchived setting
  const candidateWorkstreams = showArchived ? allWorkstreams : activeWorkstreams;

  // The active selection list based on showArchived
  const selectedIDs = showArchived ? selectedWithArchived : selectedWithoutArchived;
  const setSelectedIDs = showArchived ? setSelectedWithArchived : setSelectedWithoutArchived;

  // Compute which workstreams to display
  const filteredWorkstreams = useMemo(() => {
    if (showAll) {
      return candidateWorkstreams;
    }
    if (!selectedIDs) {
      // No saved list — default to all checked
      return candidateWorkstreams;
    }
    const set = new Set(selectedIDs);
    return candidateWorkstreams.filter((ws) => set.has(ws.workstreamID));
  }, [showAll, candidateWorkstreams, selectedIDs]);

  // When toggling showAll off and no saved list, initialize it to all IDs
  const handleToggleShowAll = useCallback((v: boolean) => {
    if (!v) {
      // Turning off "Show All" — ensure we have a selection list
      const currentList = showArchived ? selectedWithArchived : selectedWithoutArchived;
      if (!currentList) {
        const allIDs = candidateWorkstreams.map((ws) => ws.workstreamID);
        if (showArchived) setSelectedWithArchived(allIDs);
        else setSelectedWithoutArchived(allIDs);
      }
    }
    setShowAll(v);
  }, [showArchived, selectedWithArchived, selectedWithoutArchived, candidateWorkstreams, setShowAll, setSelectedWithArchived, setSelectedWithoutArchived]);

  const handleToggleWorkstream = useCallback((wsID: string) => {
    const current = selectedIDs ?? candidateWorkstreams.map((ws) => ws.workstreamID);
    const next = current.includes(wsID) ? current.filter((id) => id !== wsID) : [...current, wsID];
    setSelectedIDs(next);
  }, [selectedIDs, candidateWorkstreams, setSelectedIDs]);

  const handleSelectAll = useCallback((visibleIDs: string[]) => {
    const current = selectedIDs ?? candidateWorkstreams.map((ws) => ws.workstreamID);
    const currentSet = new Set(current);
    for (const id of visibleIDs) currentSet.add(id);
    setSelectedIDs(Array.from(currentSet));
  }, [selectedIDs, candidateWorkstreams, setSelectedIDs]);

  const handleSelectNone = useCallback((visibleIDs: string[]) => {
    const current = selectedIDs ?? candidateWorkstreams.map((ws) => ws.workstreamID);
    const removeSet = new Set(visibleIDs);
    setSelectedIDs(current.filter((id) => !removeSet.has(id)));
  }, [selectedIDs, candidateWorkstreams, setSelectedIDs]);

  // Determine if "no workstreams selected" should show
  const effectivePageState = useMemo((): PageState => {
    if (pageState !== 'workstreams-view') return pageState;
    if (filteredWorkstreams.length === 0) return 'no-workstreams-selected';
    return 'workstreams-view';
  }, [pageState, filteredWorkstreams]);

  const handleCreate = useCallback(async () => {
    if (creating) return;
    setCreating(true);
    try {
      const resp = await fetch('/api/workstreams', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Name: 'New Workstream' }),
      });
      if (!resp.ok) {
        setError((await resp.text()) || 'Failed to create workstream');
        return;
      }
      await loadWorkstreams();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create workstream');
    } finally {
      setCreating(false);
    }
  }, [creating, loadWorkstreams]);

  if (error) {
    return <div style={{ padding: '20px 16px', color: 'red' }}>{error}</div>;
  }

  if (pageState === 'loading') {
    return <div style={{ padding: '20px 16px' }}>Loading...</div>;
  }

  return (
    <div style={{ display: 'flex', minHeight: '100%', position: 'relative' }}>
      {/* Collapsed search sidebar */}
      <div
        style={{
          width: '32px', flexShrink: 0, borderRight: '1px solid #d0d7de',
          display: 'flex', justifyContent: 'center', paddingTop: '20px',
          cursor: 'pointer', zIndex: 20, backgroundColor: '#fff',
        }}
        onClick={() => setFilterOpen(true)}
        title="Open filters"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#57606a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
      </div>

      {/* Filter overlay panel */}
      {filterOpen && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 1100 }} onClick={() => setFilterOpen(false)} />
          <div
            ref={filterRef}
            style={{
              position: 'absolute', top: 0, left: '32px', width: '300px', height: '100%',
              backgroundColor: '#fff', borderRight: '1px solid #d0d7de', zIndex: 1101,
              padding: '16px', boxShadow: '2px 0 8px rgba(0,0,0,0.1)', overflowY: 'auto',
            }}
          >
            <WorkstreamFilterPanel
              workstreams={candidateWorkstreams}
              selectedIDs={selectedIDs}
              showAll={showAll}
              showArchived={showArchived}
              onToggleShowAll={handleToggleShowAll}
              onToggleShowArchived={setShowArchived}
              onToggleWorkstream={handleToggleWorkstream}
              onSelectAll={handleSelectAll}
              onSelectNone={handleSelectNone}
              onClose={() => setFilterOpen(false)}
              taskSearch={taskSearch}
              onTaskSearchChange={setTaskSearch}
              taskAssignees={taskAssignees}
              onToggleTaskAssignee={handleToggleTaskAssignee}
              showArchivedTasks={showArchivedTasks}
              onToggleShowArchivedTasks={setShowArchivedTasks}
              userInitials={userInitials}
            />
          </div>
        </>
      )}

      {/* Main content */}
      <div style={{ flex: 1, paddingTop: '20px', paddingLeft: '16px', paddingRight: '16px' }}>
        {effectivePageState === 'no-workstreams-yet' && (
          <>
            <h2 style={{ fontSize: '20px', fontWeight: 600, margin: '0 0 8px 0' }}>No Workstreams yet!</h2>
            <p style={{ fontSize: '14px', color: '#57606a', margin: '0 0 16px 0', maxWidth: '480px' }}>
              Workstreams allow you to define sequences of tasks for Plan 42 to work on.
              To get started you can either create a workstream manually or ask Plan42 to help you.
            </p>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button type="button" disabled={creating} onClick={handleCreate}
                style={{ ...buttonStyle, cursor: creating ? 'wait' : 'pointer' }}>
                <span style={{ color: '#4ade80', fontSize: '16px', lineHeight: 1 }}>+</span>
                Create a new Workstream
              </button>
              <button type="button" onClick={() => navigate('/tasks/new')} style={buttonStyle}>
                Plan a Workstream using AI
              </button>
            </div>
          </>
        )}

        {effectivePageState === 'no-workstreams-selected' && (
          <>
            <h2 style={{ fontSize: '20px', fontWeight: 600, margin: '0 0 8px 0' }}>No Workstreams selected!</h2>
            <p style={{ fontSize: '14px', color: '#57606a', margin: '0 0 16px 0', maxWidth: '480px' }}>
              You don&apos;t have any workstreams selected. You can either select workstreams to view, or create a new one.
            </p>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button type="button" onClick={() => setFilterOpen(true)} style={buttonStyle}>Select Workstreams</button>
              <button type="button" disabled={creating} onClick={handleCreate}
                style={{ ...buttonStyle, cursor: creating ? 'wait' : 'pointer' }}>
                <span style={{ color: '#4ade80', fontSize: '16px', lineHeight: 1 }}>+</span>
                Create a new Workstream
              </button>
              <button type="button" onClick={() => navigate('/tasks/new')} style={buttonStyle}>
                Plan a Workstream using AI
              </button>
            </div>
          </>
        )}

        {effectivePageState === 'workstreams-view' && (
          <div>
            <div
              style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', cursor: 'pointer' }}
              onClick={handleCreate}
            >
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: '24px', height: '24px', borderRadius: '50%',
                backgroundColor: '#2da44e', color: '#fff', fontSize: '18px', fontWeight: 'bold', lineHeight: 1,
              }}>+</span>
              <span style={{ fontSize: '14px', fontWeight: 500 }}>Add New Workstream</span>
            </div>
            {filteredWorkstreams.map((ws) => (
              <WorkstreamCard key={ws.workstreamID} workstream={ws} onWorkstreamChanged={loadWorkstreams} taskFilter={taskFilter} userTenantID={userTenantID} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
